import type {
  CRMUser,
  PeopleTravelEvent,
  PeopleTravelLeg,
  PeopleTravelLocation,
} from '../types';
import type { TripPlanDraft } from './peopleTravelPlanService';
import {
  formatCanvasSelectionLabel,
  travelCanvasSnapshotJson,
  type TravelCanvasSelection,
} from './peopleTravelCanvasGraph';
import { peopleTravelMemberLabel } from './peopleTravelTeamMembers';

export type TripPlanChatTurn = {
  id: string;
  role: 'user' | 'planner';
  text: string;
  imageCount?: number;
  createdAt: number;
};

export function mergeTripPlanDrafts(
  previous: TripPlanDraft | null,
  next: TripPlanDraft,
): TripPlanDraft {
  return {
    title: next.title || previous?.title,
    purpose: next.purpose || previous?.purpose,
    startDate: next.startDate || previous?.startDate,
    endDate: next.endDate || previous?.endDate,
    destinations: next.destinations?.length ? next.destinations : previous?.destinations,
    locations: next.locations?.length ? next.locations : previous?.locations,
    events: next.events?.length ? next.events : previous?.events,
    legs: next.legs?.length ? next.legs : previous?.legs,
    travelerUserIds: next.travelerUserIds?.length
      ? next.travelerUserIds
      : previous?.travelerUserIds,
    budgetCap: next.budgetCap ?? previous?.budgetCap,
    summary: next.summary || previous?.summary,
    openQuestions: next.openQuestions?.length ? next.openQuestions : previous?.openQuestions,
  };
}

export function tripPlanDraftIsReady(draft: TripPlanDraft | null): boolean {
  if (!draft) return false;
  const title = draft.title?.trim() || '';
  const hasDest = Boolean(draft.destinations?.some(d => d.label.trim()));
  const hasTitle = Boolean(title) && !/attached image not received|^untitled$/i.test(title);
  return hasDest || (hasTitle && Boolean(draft.startDate && draft.endDate));
}

export function formatTripPlanReadyLines(
  draft: TripPlanDraft,
  teamMembers: CRMUser[],
): string[] {
  const travelerNames = (draft.travelerUserIds || [])
    .map(uid => {
      const member = teamMembers.find(u => u.uid === uid);
      return member ? peopleTravelMemberLabel(member) : uid;
    })
    .filter(Boolean);
  const lines = [
    draft.title?.trim() || 'Untitled trip',
    [draft.startDate, draft.endDate].filter(Boolean).join(' → '),
    (draft.destinations || []).map(d => d.label).filter(Boolean).join(' · '),
  ].filter(Boolean);
  if (travelerNames.length) lines.push(travelerNames.join(', '));
  return lines;
}

export type TripPlanChainMode = 'build' | 'rebuild' | 'edit';

export function resolveTripPlanChainMode(args: {
  chainMode?: TripPlanChainMode;
  locations?: PeopleTravelLocation[];
  events?: PeopleTravelEvent[];
  legs?: PeopleTravelLeg[];
}): TripPlanChainMode {
  if (args.chainMode) return args.chainMode;
  const hasCanvas = Boolean(args.locations?.length || args.events?.length || args.legs?.length);
  return hasCanvas ? 'edit' : 'build';
}

export function buildTripPlanConversationPrompt(args: {
  turns: TripPlanChatTurn[];
  latestText: string;
  imageCount: number;
  teamMembers: CRMUser[];
  selection?: TravelCanvasSelection | null;
  locations?: PeopleTravelLocation[];
  events?: PeopleTravelEvent[];
  legs?: PeopleTravelLeg[];
  chainMode?: TripPlanChainMode;
}): string {
  const roster = args.teamMembers
    .map(u => `- ${peopleTravelMemberLabel(u)}`)
    .join('\n');
  const history = args.turns
    .map(turn => `${turn.role === 'user' ? 'Staff' : 'Planner'}: ${turn.text}`)
    .join('\n\n');
  const latest = args.latestText.trim() || '(no typed notes — see attached screenshot)';
  const imageNote =
    args.imageCount > 0
      ? `\nThis latest message includes ${args.imageCount} screenshot(s). Read them.`
      : '';
  const hasCanvas = Boolean(args.locations?.length || args.events?.length || args.legs?.length);
  const chainMode = resolveTripPlanChainMode(args);
  const scope = hasCanvas
    ? formatCanvasSelectionLabel(
        args.selection || null,
        args.locations || [],
        args.events || [],
        args.legs || [],
      )
    : '';
  const canvasBlock = hasCanvas
    ? [
        chainMode === 'rebuild'
          ? 'Rebuild the whole trip chain from this message.'
          : `Selected canvas card: ${scope}`,
        'Current trip canvas (keep existing ids when they still apply):',
        travelCanvasSnapshotJson({
          locations: args.locations || [],
          events: args.events || [],
          legs: args.legs || [],
          selection: chainMode === 'edit' ? args.selection : null,
        }),
      ].join('\n')
    : 'The board is empty. Lay out the first visual chain.';

  const chainRule =
    chainMode === 'rebuild'
      ? '- Rebuild the full chain. Return complete locations[], events[], and legs[] arrays for the whole trip. You may keep ids that still apply.'
      : chainMode === 'build'
        ? '- Build the visual chain on the board: location clusters connected by travel legs, with events inside each place. Return locations[], events[], and legs[] for that whole chain.'
        : '- A board card is selected. Change that section only unless they asked to redo the whole trip. Return locations[], events[], and legs[] for anything you add or change. Keep ids of cards you rewrite.';

  return [
    'You are the LCTF trip planner in a live CRM chat. This is a back-and-forth conversation, not a one-shot form fill.',
    roster ? `Team roster (match traveler names to these people):\n${roster}` : '',
    history ? `Prior conversation:\n${history}` : 'This is the first message.',
    `Latest staff message:\n"""\n${latest}\n"""${imageNote}`,
    canvasBlock,
    [
      'Rules:',
      '- Put your next chat reply in summary. Talk to the staff member. Keep it short.',
      '- Ask at most two questions. Do not write a logistics essay unless they asked for one.',
      '- Fill draft fields you know. Omit startDate/endDate if the calendar week is still unknown.',
      '- destinations, locations, events, and legs must be JSON arrays of objects, never strings. Right: [{"label":"Ortanna, PA"}].',
      '- Do not invent a street address. If the jobsite is unnamed, ask.',
      chainRule,
    ].join('\n'),
  ]
    .filter(Boolean)
    .join('\n\n');
}
